import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { useNavigate } from "react-router-dom";

const SEGMENTS = ["CASH", "UPI", "WALLET", "CREDIT", "OTHER"];

export default function Revenue() {
  const navigate = useNavigate();
  const [activeSegment, setActiveSegment] = useState(null);
  const [range, setRange] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRevenue(); }, [range, customFrom, customTo]);

  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      let dateFrom = today;
      let dateTo = today;
      if (range === "week") {
        const d = new Date();
        d.setDate(d.getDate() - 6);
        dateFrom = d.toISOString().slice(0, 10);
      } else if (range === "month") {
        const d = new Date();
        d.setDate(d.getDate() - 29);
        dateFrom = d.toISOString().slice(0, 10);
      } else if (range === "custom") {
        if (!customFrom || !customTo) { setLoading(false); return; }
        dateFrom = customFrom;
        dateTo = customTo;
      }
      const res = await api.get(`/v2/payments/reports/payment-summary?date_from=${dateFrom}&date_to=${dateTo}`);
      setSummary(res.data.summary);
      setItems(res.data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const grandPaidTotal = summary
    ? SEGMENTS.reduce((sum, seg) => sum + (summary[seg]?.paid_total || 0), 0)
    : 0;
  const grandPendingTotal = summary
    ? SEGMENTS.reduce((sum, seg) => sum + (summary[seg]?.pending_total || 0), 0)
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Revenue</h1>
          <p className="text-slate-500 text-sm mt-0.5">Earnings by payment method, with paid vs pending.</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-center gap-3">
          {[
            { key: "today", label: "Today" },
            { key: "week", label: "This Week" },
            { key: "month", label: "This Month" },
            { key: "custom", label: "Custom" },
          ].map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${range === r.key ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {r.label}
            </button>
          ))}
          {range === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="px-2 py-1 border border-slate-200 rounded text-xs" />
              <span className="text-slate-400 text-xs">to</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="px-2 py-1 border border-slate-200 rounded text-xs" />
            </div>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400">Loading...</div>
        ) : summary ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Total Received</p>
                <p className="text-2xl font-heading font-bold text-emerald-600">₹{grandPaidTotal.toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Total Pending</p>
                <p className="text-2xl font-heading font-bold text-amber-600">₹{grandPendingTotal.toFixed(2)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {SEGMENTS.map((seg) => (
  <button key={seg} onClick={() => setActiveSegment(activeSegment === seg ? null : seg)}
    className={`bg-white rounded-lg border p-4 text-left transition-colors ${activeSegment === seg ? "border-brand-500 ring-2 ring-brand-100" : "border-slate-200 hover:border-slate-300"}`}>
    <p className="text-xs font-semibold text-slate-400 uppercase mb-2">{seg}</p>
    <p className="text-xl font-heading font-bold text-emerald-600">₹{summary[seg]?.paid_total?.toFixed(2) || "0.00"}</p>
    <p className="text-[11px] text-slate-400 mb-2">{summary[seg]?.paid_count || 0} paid</p>
    {summary[seg]?.pending_count > 0 && (
      <p className="text-[11px] text-amber-600">₹{summary[seg]?.pending_total?.toFixed(2)} pending ({summary[seg]?.pending_count})</p>
    )}
  </button>
))}
            </div>

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-heading font-semibold text-lg text-slate-800 uppercase">Transactions</h2>
              </div>
              {items.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No transactions in this range.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {["Order ID", "Segment", "Amount", "Status", "Date"].map((h) => (
                          <th key={h} className="px-3 py-2 text-xs font-bold text-slate-400 uppercase text-left">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {items
  .filter((it) => !activeSegment || it.segment === activeSegment)
  .map((it, i) => (
    <tr key={i} onClick={() => navigate(`/admin/shipments/${it.shipment_id}/tracking`)}
      className="hover:bg-slate-50 cursor-pointer">
      <td className="px-3 py-2 text-xs font-semibold text-brand-600">{it.order_id}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{it.segment}</td>
      <td className="px-3 py-2 text-xs text-slate-700">₹{it.amount?.toFixed(2)}</td>
      <td className="px-3 py-2 text-xs">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${it.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {it.payment_status}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">{it.created_at ? new Date(it.created_at).toLocaleDateString("en-IN") : "—"}</td>
    </tr>
  ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
